import React from "react";
import t from "../../i18n";
import type { Project } from "../../api/useProjects";
import { useSetups } from "../../api/useSetups";
import { ProjectDashboardNavLink } from "./Dashboards/component/ProjectNavLink";
import Tab from "@ui/NavV2/Tab";
import NavV2 from "@ui/NavV2";
import { useFaultEventAccess } from "./Events/hooks/useFaultEventAccess";
import { toPath, useIsCurrentRouteMatching } from "../../router/helpers/path";
import { useSettings } from "../../api/useSettings";

interface P {
  project: Project;
  reactiveProp: string;
}

const ProjectNavBar = ({ project, reactiveProp }: P) => {
  const projectId = project.id;
  const setups = useSetups(projectId);
  const hasSetup = setups.length > 0;
  const hasFaultEventAccess = useFaultEventAccess();
  const settings = useSettings();

  const isDiagnosisPage = useIsCurrentRouteMatching("/projects/:projectId/diagnosis");
  const isSymptomsPage = useIsCurrentRouteMatching("/projects/:projectId/symptoms");
  const isMeasuresPage = useIsCurrentRouteMatching("/projects/:projectId/measures");

  const links = React.useMemo(
    () =>
      settings.NEW_NAVIGATION_ENABLED === false ? (
        <>
          {hasSetup && (
            <Tab
              key="summary"
              to={toPath("/projects/:projectId/summary", {
                projectId,
              })}
              data-cy="summary-tab"
              theme="dark"
            >
              {t("project.document.title.summary")}
            </Tab>
          )}
          <Tab
            key="measures"
            to={toPath("/projects/:projectId/measures", { projectId })}
            data-cy="measures-tab"
            theme="dark"
          >
            {t("project.measures.title")}
          </Tab>
          {hasSetup && (
            <>
              <Tab
                key="symptoms"
                to={toPath("/projects/:projectId/symptoms", { projectId })}
                data-cy="symptoms-tab"
                theme="dark"
              >
                {t("project.symptoms.title")}
              </Tab>
              <Tab
                key="diagnosis"
                to={toPath("/projects/:projectId/diagnosis", { projectId })}
                data-cy="diagnosis-tab"
                theme="dark"
              >
                {t("project.document.title.diagnosis")}
              </Tab>
              {hasFaultEventAccess ? (
                <Tab
                  key="faultEvents"
                  to={toPath("/projects/:projectId/fault-events", { projectId })}
                  data-cy="fault-events-tab"
                  theme="dark"
                >
                  {t("project.document.title.fault_events")}
                </Tab>
              ) : null}
            </>
          )}
          <ProjectDashboardNavLink projectId={projectId} />
        </>
      ) : (
        <>
          {hasSetup && (
            <Tab
              key="summary"
              to={toPath("/projects/:projectId/summary", {
                projectId,
              })}
              data-cy="summary-tab"
              theme="dark"
            >
              {t("project.document.title.new_navigation.summary")}
            </Tab>
          )}
          {hasSetup && (
            <>
              {hasFaultEventAccess ? (
                <Tab
                  key="faultEvents"
                  to={toPath("/projects/:projectId/fault-events", { projectId })}
                  data-cy="fault-events-tab"
                  theme="dark"
                >
                  {t("project.document.title.fault_events")}
                </Tab>
              ) : null}
            </>
          )}
          <ProjectDashboardNavLink projectId={projectId} />
          <Tab
            key="measures"
            to={toPath("/projects/:projectId/measures", { projectId })}
            data-cy="measures-tab"
            theme="dark"
            active={isDiagnosisPage || isSymptomsPage || isMeasuresPage}
          >
            {t("project.document.title.new_navigation.exploration")}
          </Tab>
        </>
      ),
    [
      projectId,
      hasSetup,
      hasFaultEventAccess,
      settings,
      isDiagnosisPage,
      isSymptomsPage,
      isMeasuresPage,
    ],
  );

  return <NavV2 reactiveProp={reactiveProp}>{links}</NavV2>;
};

export default React.memo(ProjectNavBar);
